/**
 * Hook to detect and fetch airports for geographic regions
 * VOY-101: Multi-airport region support
 */

import { useState, useCallback } from 'react';

interface RegionAirport {
  code: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  airport_name: string;
  timezone: string;
  distanceFromCenter: number;
}

interface RegionAirportsResponse {
  success: boolean;
  data: {
    region: string;
    country: string | null;
    airports: RegionAirport[];
    count: number;
  };
}

const API_URL = import.meta.env.DEV 
  ? 'http://localhost:3000' 
  : 'https://backend-test-ldkc.onrender.com';

/**
 * Map of known regions to their country codes
 */
const REGION_TO_COUNTRY: Record<string, string> = {
  // US States
  'california': 'US',
  'florida': 'US',
  'texas': 'US',
  'new york': 'US',
  'nevada': 'US',
  'arizona': 'US',
  'colorado': 'US',
  'washington': 'US',
  'oregon': 'US',
  'illinois': 'US',
  'massachusetts': 'US',
  'pennsylvania': 'US',
  'georgia': 'US',
  'north carolina': 'US',
  'virginia': 'US',
  'ohio': 'US',
  'michigan': 'US',
  'tennessee': 'US',
  'louisiana': 'US',
  'alabama': 'US',
  'south carolina': 'US',
  'kentucky': 'US',
  'minnesota': 'US',
  'wisconsin': 'US',
  'missouri': 'US',
  'maryland': 'US',
  'indiana': 'US',
  'hawaii': 'US',
  'alaska': 'US',
  'utah': 'US',
  'new mexico': 'US',
  'montana': 'US',
  
  // US Metro areas
  'bay area': 'US',
  'silicon valley': 'US',
  'new england': 'US',
  'midwest': 'US',
  'pacific northwest': 'US',
  'tri-state': 'US',
  'socal': 'US',
  'norcal': 'US',
  'south florida': 'US',
  'central florida': 'US',
  
  // European regions
  'catalonia': 'ES',
  'andalusia': 'ES',
  'tuscany': 'IT',
  'bavaria': 'DE',
  'provence': 'FR'
};

/**
 * Check if a query is likely a region (state/province) rather than a specific city/airport
 */
function isLikelyRegion(query: string): boolean {
  const normalized = query.trim().toLowerCase();
  
  // Check for exact matches in our known regions
  if (normalized in REGION_TO_COUNTRY) {
    return true;
  }
  
  // Check for partial matches (e.g., "northern california")
  const regionKeywords = ['northern', 'southern', 'eastern', 'western', 'central'];
  for (const keyword of regionKeywords) {
    if (normalized.startsWith(keyword + ' ')) {
      return true;
    }
  }
  
  return false;
}

/**
 * Detect country code from region name
 */
function detectCountryFromRegion(regionName: string): string | undefined {
  const normalized = regionName.trim().toLowerCase();
  return REGION_TO_COUNTRY[normalized];
}

export function useRegionAirports() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch airports for a region
   * @param regionName - The region to search (e.g., "California", "Tuscany")
   * @param countryCode - Optional country code. If not provided, will auto-detect from regionName
   */
  const getRegionAirports = useCallback(async (
    regionName: string,
    countryCode?: string
  ): Promise<RegionAirport[]> => {
    setLoading(true);
    setError(null);

    try {
      // Auto-detect country if not provided
      const country = countryCode || detectCountryFromRegion(regionName) || 'US';
      
      const url = `${API_URL}/api/location/airports/region?region=${encodeURIComponent(regionName)}&country=${country}`;
      console.log(`[useRegionAirports] Fetching: ${url} (country: ${country}${!countryCode ? ' auto-detected' : ''})`);

      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: RegionAirportsResponse = await response.json();

      if (!data.success) {
        throw new Error('Failed to fetch region airports');
      }

      console.log(`[useRegionAirports] ✅ Found ${data.data.count} airports in ${regionName}`);
      return data.data.airports;

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      console.error('[useRegionAirports] Error:', errorMsg);
      setError(errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check if a query should trigger region airport search
   */
  const shouldSearchRegion = useCallback((query: string): boolean => {
    if (!query || query.length < 3) return false;
    return isLikelyRegion(query);
  }, []);

  return {
    getRegionAirports,
    shouldSearchRegion,
    loading,
    error
  };
}

